import type { ApiResponse } from "../../../types/apiResponse";

type dataResponse = {
  UUID_file: string
}

export const uploadFile = async (
  file: File,
  endpoint: string,
  token?: string
): Promise<ApiResponse<dataResponse>> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(endpoint, {
    method: "POST",
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : undefined,
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return response.json();
};

export const downloadFileFetch = async (
  endpoint: string,
  token?: string
): Promise<void> => {
  const response = await fetch(endpoint, {
    method: "GET",
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : undefined,
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  const { status, data, message } = await response.json();

  if (status !== 200) {
    throw new Error(`Download failed: ${message}`);
  }

  if (data.length == 0) {
    throw new Error(`Empty data`);
  }
  const { file_content, name, type } = data[0];

  if (!file_content) {
    throw new Error("File content is empty.");
  }

  try {
    // Remove data URI prefix if present
    const base64 = file_content.includes(",")
      ? file_content.split(",")[1]
      : file_content;

    // Decode Base64
    const binary = atob(base64.replace(/\s/g, ""));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

    // Create Blob
    const blob = new Blob([bytes], { type: type || "application/octet-stream" });

    // Download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = name || "download";
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Base64 decoding failed:", error);
    throw new Error("Invalid Base64 file data.");
  }
};

export const getPdfUrlFetch = async (
  endpoint: string,
  token?: string
): Promise<string> => {
  const response = await fetch(endpoint, {
    method: "GET",
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : undefined,
  });

  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.statusText}`);
  }

  const { status, data, message } = await response.json();

  if (status !== 200) {
    throw new Error(`Fetch failed: ${message}`);
  }

  if (data.length == 0) {
    throw new Error(`Empty data`);
  }
  const { file_content, type } = data[0];

  if (!file_content) {
    throw new Error("File content is empty.");
  }

  try {
    const base64 = file_content.includes(",")
      ? file_content.split(",")[1]
      : file_content;

    const binary = atob(base64.replace(/\s/g, ""));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

    const blob = new Blob([bytes], { type: type || "application/pdf" });

    return window.URL.createObjectURL(blob);
  } catch (error) {
    console.error("Base64 decoding failed:", error);
    throw new Error("Invalid Base64 file data.");
  }
};

/**
 * Descarga un archivo del servicio de archivos y lo devuelve como **data URI**
 * (`data:<mime>;base64,...`).
 *
 * A diferencia de `getPdfUrlFetch`, que crea un `blob:` URL, aquí se devuelve el
 * base64 embebido porque `@react-pdf/renderer` necesita poder resolver la imagen
 * tanto en la vista previa como al generar el PDF descargable.
 */
export const getFileDataUrlFetch = async (
  endpoint: string,
  token?: string
): Promise<string> => {
  const response = await fetch(endpoint, {
    method: "GET",
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : undefined,
  });

  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.statusText}`);
  }

  const { status, data, message } = await response.json();

  if (status !== 200) {
    throw new Error(`Fetch failed: ${message}`);
  }

  if (!data || data.length === 0) {
    throw new Error("Empty data");
  }

  const { file_content, type } = data[0];

  if (!file_content) {
    throw new Error("File content is empty.");
  }

  // El servicio puede regresar el base64 crudo o ya con el prefijo data URI.
  if (file_content.startsWith("data:")) {
    return file_content;
  }

  const base64 = file_content.includes(",")
    ? file_content.split(",")[1]
    : file_content;

  return `data:${type || "application/octet-stream"};base64,${base64.replace(/\s/g, "")}`;
};
